import { useEffect, useRef, useState } from "react";
import type { TailgateConfig, TailgateRound } from "../scenario/types";
import { audio } from "../lib/audio";
import { ChoiceCard, ChoiceList } from "../ui/ChoiceCard";
import { activeTtsLang } from "../lib/locale";

/**
 * A persistent social engineer at the badge-gated side door (office-under-attack
 * Episode 1). Unlike Intercom/TechRequest this is a GENUINE branching decision
 * with a real refuse path — but the visitor does NOT give up after one "no".
 *
 * When `config.rounds` is set the encounter ESCALATES: a friendly courier with
 * their hands full -> name-dropping an executive and "reception is expecting me"
 * -> open pressure and thinly-veiled threats ("I just spoke to your boss...
 * what's your name?"). Complying at ANY round opens the door (`onEnded("risky")`);
 * holding firm all the way through the final round makes the visitor storm off
 * (`onEnded("safe")`). This models how confident impersonators talk their way in
 * and why "just say no once" isn't enough — you have to stay firm under pressure.
 *
 * `onEnded("risky"|"safe")` mirrors VideoCall/DeepfakeCall so the scenario graph
 * can route a risky pick into a consequence-and-rewind loop.
 *
 * When `config.station` is set this renders the door-entry CCTV feed look
 * (reuses Intercom's `.ic-*` classes: live face clip under a scanline/flicker
 * overlay). Single-round back-compat (`message`/`clipUrl`) is preserved.
 */
export function Tailgate({
  config,
  onEnded,
}: {
  config: TailgateConfig;
  onEnded: (outcomeKey: "risky" | "safe") => void;
}) {
  const showCctv = !!config.station;

  // Normalize to a rounds[] list. If the author gave explicit rounds, use them;
  // otherwise synthesize a single round from the legacy flat fields.
  const rounds: TailgateRound[] =
    config.rounds && config.rounds.length
      ? config.rounds
      : [
          {
            id: "only",
            clipUrl: config.forceSafe && config.clipPressureUrl ? config.clipPressureUrl : config.clipUrl,
            message: config.message,
            complyLabel: config.holdDoorLabel,
            refuseLabel: config.refuseLabel,
          },
        ];

  const [roundIdx, setRoundIdx] = useState(0);
  // After the learner holds firm on the final round, the visitor storms off:
  // we play a give-up clip, then a single button records the "safe" outcome.
  const [giveup, setGiveup] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const started = useRef(false);

  const round = rounds[Math.min(roundIdx, rounds.length - 1)];
  const isFinal = roundIdx >= rounds.length - 1;
  const currentClip = giveup ? config.giveupClipUrl : round.clipUrl;
  const currentMessage = giveup ? config.giveupMessage ?? "Fine — I'll go sign in at reception." : round.message;

  // Kick off badge beep + first clip once.
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    audio.badgeBeep();
    window.setTimeout(() => {
      const v = videoRef.current;
      if (v && currentClip) {
        playClipWithSound(v);
      } else if (!currentClip) {
        audio.speak(currentMessage, { voice: config.voiceId ?? "onyx", lang: config.language ?? activeTtsLang() });
      }
    }, 650);
    return () => audio.cancelSpeech();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the round (or give-up state) changes, swap the clip and play it (same
  // panel, so the face keeps moving and the voice stays the same person).
  useEffect(() => {
    if (!started.current) return;
    const v = videoRef.current;
    if (v && currentClip) {
      try {
        v.load();
      } catch {
        /* ignore */
      }
      playClipWithSound(v);
    } else if (!currentClip) {
      audio.speak(currentMessage, { voice: config.voiceId ?? "onyx", lang: config.language ?? activeTtsLang() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundIdx, giveup]);

  /** Plays the visitor clip with its own embedded voice audible. Some browsers
   *  reject an unmuted `.play()` outright (autoplay-with-sound policy) even
   *  though the SAME page already has "sticky" user activation from the
   *  intro card's Begin-mission click — the rejection then left the clip
   *  silently un-played (2026-07-26 regression report: "we cannot hear the
   *  voice on the intercom anymore"). Falls back to the standard
   *  muted-autoplay-then-unmute trick, which browsers always allow. */
  function playClipWithSound(v: HTMLVideoElement) {
    v.volume = 1;
    v.muted = !audio.isEnabled();
    v.play().catch(() => {
      if (!audio.isEnabled()) return; // learner has audio off — stay silent, don't force it on
      v.muted = true;
      v.play()
        .then(() => {
          window.setTimeout(() => {
            v.muted = false;
          }, 60);
        })
        .catch(() => {});
    });
  }

  const initials =
    config.visitorInitials ??
    config.visitorName
      .split(/\s+/)
      .map((p) => p[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const comply = () => {
    audio.doorOpen();
    onEnded("risky");
  };

  const pushBack = () => {
    if (config.forceSafe) {
      // Retry beat: single-round, refuse ends safe immediately.
      audio.doorClose();
      onEnded("safe");
      return;
    }
    if (isFinal) {
      // Held firm through the last round — the visitor gives up and leaves.
      if (config.giveupClipUrl) {
        audio.cancelSpeech();
        setGiveup(true);
      } else {
        audio.doorClose();
        onEnded("safe");
      }
    } else {
      // Not the last word — the visitor escalates.
      audio.cancelSpeech();
      setRoundIdx((i) => i + 1);
    }
  };

  const refuseCat = round.refuseCategory ?? (isFinal ? "refuse" : "verify");

  return (
    <div className="tailgate">
      <div className="tg-panel">
        {showCctv ? (
          <div className="ic-station">
            <div className="ic-head">
              <span className="ic-dot" aria-hidden />
              <span className="ic-title">{config.station ?? "Side Entrance"}</span>
              <span className="ic-live">LIVE</span>
            </div>
            <div className="ic-feed">
              {currentClip ? (
                <video
                  ref={videoRef}
                  className="ic-video"
                  src={currentClip}
                  playsInline
                  preload="auto"
                  onError={() => {
                    audio.speak(currentMessage, {
                      voice: config.voiceId ?? "onyx",
                      lang: config.language ?? activeTtsLang(),
                    });
                  }}
                />
              ) : (
                <>
                  <div className="ic-feed-noise" aria-hidden />
                  <div className="ic-avatar">{initials}</div>
                </>
              )}
              <div className="ic-cctv" aria-hidden>
                <div className="ic-scan" />
                <div className="ic-roll" />
                <div className="ic-flicker" />
                <div className="ic-vig" />
              </div>
              <div className="ic-feed-meta">
                <span className="ic-rec" aria-hidden />
                CAM · DOOR
              </div>
              {!giveup && rounds.length > 1 && !config.forceSafe && (
                <div className="tg-round">Attempt {roundIdx + 1} of {rounds.length}</div>
              )}
            </div>
          </div>
        ) : (
          <div className="tg-head">
            <span className="tg-avatar" aria-hidden>
              {initials}
            </span>
            <div className="tg-id">
              <strong>{config.visitorName}</strong>
              {config.visitorOrg && <span className="tg-org">{config.visitorOrg}</span>}
            </div>
          </div>
        )}

        <div className="tg-caption">
          {showCctv && (
            <div className="ic-who">
              <strong>{config.visitorName}</strong>
              {(giveup ? "Leaving" : round.tag ?? config.visitorOrg) && (
                <span className="ic-org">{giveup ? "Leaving" : round.tag ?? config.visitorOrg}</span>
              )}
            </div>
          )}
          <p className="tg-msg">&ldquo;{currentMessage}&rdquo;</p>
          {!giveup && config.subtext && roundIdx === 0 && <p className="tg-sub">{config.subtext}</p>}
          {config.forceSafe && config.retryHint && (
            <p className="tg-hint">{config.retryHint}</p>
          )}
        </div>

        <div className="tg-actions">
          {giveup ? (
            <ChoiceList>
              <ChoiceCard
                category="refuse"
                reply="Close the door and log the attempt"
                onClick={() => {
                  audio.doorClose();
                  onEnded("safe");
                }}
              />
            </ChoiceList>
          ) : (
            <ChoiceList>
              {!config.forceSafe && (
                <ChoiceCard
                  category="comply"
                  reply={round.complyLabel ?? config.holdDoorLabel ?? "Let them in"}
                  onClick={comply}
                />
              )}
              <ChoiceCard
                category={refuseCat}
                reply={round.refuseLabel ?? config.refuseLabel ?? "Ask them to check in at reception first"}
                onClick={pushBack}
              />
            </ChoiceList>
          )}
        </div>
      </div>
    </div>
  );
}
